import { useState, useCallback, useMemo } from 'react'

import CardDto from '../interfaces/CardDto'

interface UseVehicleSelectionProps {
  vehicles: CardDto[]
  onBulkPublish?: (vehicleIds: string[]) => void
}

export const useVehicleSelection = ({ vehicles, onBulkPublish }: UseVehicleSelectionProps) => {
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([])

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        // Exiting selection mode, clear selections
        setSelectedVehicleIds([])
      }

      return !prev
    })
  }, [])

  const handleSelectionChange = useCallback((vehicleId: string) => {
    setSelectedVehicleIds((prev) => {
      if (prev.includes(vehicleId)) {
        return prev.filter((id) => id !== vehicleId)
      } else {
        return [...prev, vehicleId]
      }
    })
  }, [])

  const selectAll = useCallback(() => {
    const allVehicleIds = vehicles.map((vehicle) => vehicle.id)
    setSelectedVehicleIds(allVehicleIds)
  }, [vehicles])

  const selectNone = useCallback(() => {
    setSelectedVehicleIds([])
  }, [])

  const handleBulkPublish = useCallback(() => {
    if (selectedVehicleIds.length > 0 && onBulkPublish) {
      onBulkPublish(selectedVehicleIds)
      // Keep selection mode active so user can see progress
      // They can manually exit selection mode when done
    }
  }, [selectedVehicleIds, onBulkPublish])

  const selectedCount = selectedVehicleIds.length
  const totalCount = vehicles.length

  // Only allow publishing if we have publishable vehicles selected
  const canPublish = useMemo(() => {
    const selectedVehicles = vehicles.filter((v) => selectedVehicleIds.includes(v.id))

    return selectedVehicles.length > 0
  }, [selectedVehicleIds, vehicles])

  const resetSelection = useCallback(() => {
    setSelectedVehicleIds([])
    setIsSelectionMode(false)
  }, [])

  return {
    isSelectionMode,
    selectedVehicleIds,
    selectedCount,
    totalCount,
    canPublish,
    toggleSelectionMode,
    handleSelectionChange,
    selectAll,
    selectNone,
    handleBulkPublish,
    resetSelection,
  }
}
